// Geometry of the animated logo. Circuit coordinates and lengths are in a
// 1000x1000 design space that is scaled to the canvas size when drawing.

/**
 * Arcs of the logo. Each arc is a half circle that starts at `angle` and
 * turns to `targetAngle` while the logo assembles. `alpha` is applied to the
 * logo color.
 */
export const ARCS = [
  { radius: 170, lineWidth: 15, angle: 0, targetAngle: -2.2, targetRadius: 170, alpha: 0.7 },
  { radius: 150, lineWidth: 15, angle: 1, targetAngle: -1.5, targetRadius: 150, alpha: 0.3 },
  { radius: 130, lineWidth: 15, angle: 2, targetAngle: -0.8, targetRadius: 130, alpha: 0.5 },
  { radius: 110, lineWidth: 15, angle: 3, targetAngle: 0.2, targetRadius: 110, alpha: 0.4 },
  { radius: 90, lineWidth: 15, angle: 4, targetAngle: 1.44, targetRadius: 90, alpha: 0.7 },
  { radius: 70, lineWidth: 15, angle: 5, targetAngle: 2.29, targetRadius: 70, alpha: 0.8 },
  { radius: 50, lineWidth: 15, angle: 6, targetAngle: 3.14, targetRadius: 50, alpha: 1 },
]

/**
 * Circuit lines drawn around the arcs once they are assembled. Each line starts
 * at (`x`, `y`) and follows `segments` of [length, angle in degrees]. Chip
 * outlines are drawn with a lower opacity. `startPoint` and `endPoint` add a
 * solder point at the respective end.
 *
 * Chips are drawn as hatching. Their `outline` is the package used by the 3D
 * logo: the border of the hatching, with the diagonal edge parallel to the
 * nearby octagon so the package keeps clear of its trace.
 */
export const CIRCUIT_LINES = [
  { x: 610, y: 680, width: 6, chip: true, outline: [[617, 680], [628.5, 680], [628.5, 732.1], [471.5, 732.1],
    [471.5, 715.5], [581.5, 715.5]], startPoint: false, endPoint: false, segments: [[15, 0], [5, 45],
    [45, 90], [5, 135], [150, 180], [5, 225], [13, 270], [110, 0], [45, -45], [10, 0], [43, 90], [146, 180],
    [6, 270], [107, 0], [50, -45], [36, 90], [30, 180], [34, -45], [18, 90], [10, 180], [15, -45]] },
  // Chip 1
  { x: 290, y: 200, width: 6, chip: true, outline: [[296, 200], [363.5, 200], [363.5, 154], [342, 154]],
    startPoint: false, endPoint: false, segments: [[70, 0], [5, -45],
    [44, -90], [20, -180], [59, -225], [56, 0], [36, -90], [12, -180], [48, -225], [43, 0], [31, -90],
    [5, -180], [35, -225], [25, 0], [17, -90], [17, -225], [10, 0], [5, -90]] },
  { x: 580, y: 300, width: 4, startPoint: false, endPoint: false, segments: [[170, 45], [170, 90],
    [170, 135], [170, 180], [170, 225], [170, 270], [170, 315], [170, 360]] },
  { x: 640, y: 150, width: 4, startPoint: false, endPoint: false, segments: [[300, 45], [300, 90],
    [300, 135], [300, 180], [300, 225], [300, 270], [300, 315], [300, 360]] },
  { x: 700, y: 500, width: 2, startPoint: true, endPoint: true, segments: [[70, 0], [20, 45], [40, 90],
    [96, 45]] },
  { x: 633, y: 323, width: 2, startPoint: true, endPoint: true, segments: [[43, -90], [37, -45]] },
  { x: 615, y: 305, width: 2, startPoint: true, endPoint: true, segments: [[30, -90], [28, -45]] },
  // Diagonal through-lines on the right
  { x: 770, y: 280, width: 2, startPoint: false, endPoint: false, segments: [[130, 90], [60, 45], [113, 90],
    [15, 45], [27, 0]] },
  { x: 780, y: 290, width: 2, startPoint: false, endPoint: false, segments: [[115, 90], [60, 45], [110, 90],
    [10, 45], [23, 0]] },
  { x: 790, y: 300, width: 2, startPoint: false, endPoint: false, segments: [[100, 90], [60, 45], [105, 90],
    [7, 45], [15, 0]] },
  { x: 800, y: 310, width: 2, startPoint: false, endPoint: true, segments: [[85, 90], [60, 45], [100, 90]] },
  // Short lines between the diagonal through-lines
  { x: 810, y: 335, width: 2, startPoint: true, endPoint: true, segments: [[45, 45]] },
  { x: 810, y: 355, width: 2, startPoint: true, endPoint: true, segments: [[45, 45]] },
  { x: 810, y: 370, width: 2, startPoint: true, endPoint: true, segments: [[45, 45]] },
  { x: 810, y: 390, width: 2, startPoint: true, endPoint: true, segments: [[45, 45]] },
  // Lines left of the diagonal through-lines
  { x: 800, y: 550, width: 2, startPoint: true, endPoint: true, segments: [[50, -90]] },
  { x: 800, y: 480, width: 2, startPoint: true, endPoint: true, segments: [[25, -90], [45, -135]] },
  { x: 840, y: 590, width: 2, startPoint: true, endPoint: false, segments: [[17, 45]] },
  { x: 640, y: 340, width: 2, startPoint: true, endPoint: true, segments: [[25, 45]] },
  { x: 760, y: 340, width: 2, startPoint: true, endPoint: true, segments: [[55, -225], [25, -270],
    [65, -315], [30, -270], [10, -225], [45, -180]] },
  { x: 780, y: 460, width: 2, startPoint: true, endPoint: true, segments: [[30, 90]] },
  { x: 760, y: 360, width: 2, startPoint: true, endPoint: true, segments: [[33, 135], [10, 90], [25, 45]] },
  { x: 683, y: 277, width: 2, startPoint: true, endPoint: false, segments: [[30, -45], [25, -90], [112, -135]] },
  { x: 720, y: 290, width: 2, startPoint: true, endPoint: true, segments: [[25, 0], [5, 45], [15, 90],
    [45, 135], [15, 45]] },
  { x: 730, y: 305, width: 2, startPoint: true, endPoint: true, segments: [[45, 135], [5, 90], [30, 45]] },
  { x: 680, y: 340, width: 2, startPoint: true, endPoint: true, segments: [[40, 90], [40, 45]] },
  { x: 693, y: 353, width: 2, startPoint: true, endPoint: true, segments: [[20, 90], [23, 45]] },
  { x: 700, y: 430, width: 2, startPoint: false, endPoint: true, segments: [[25, 0], [40, 45], [10, 90],
    [5, 135], [35, 180]] },
  { x: 700, y: 445, width: 2, startPoint: false, endPoint: true, segments: [[20, 0], [20, 45]] },
  { x: 680, y: 230, width: 2, startPoint: true, endPoint: false, segments: [[15, 135], [45, 180], [35, 135],
    [55, 90]] },
  { x: 660, y: 230, width: 2, startPoint: true, endPoint: false, segments: [[40, 180], [45, 135], [48, 90]] },
  { x: 660, y: 215, width: 2, startPoint: true, endPoint: false, segments: [[45, 180], [55, 135], [45, 90]] },
  { x: 605, y: 160, width: 2, startPoint: false, endPoint: false, segments: [[50, 45], [5, 90], [5, 135],
    [40, 180], [5, 225], [40, 270], [5, 315], [5, 360], [5, 45]] },
  { x: 660, y: 200, width: 2, startPoint: true, endPoint: false, segments: [[70, -135]] },
  { x: 580, y: 150, width: 2, startPoint: true, endPoint: true, segments: [[60, 90], [5, 45], [10, 0],
    [5, 90], [50, 135]] },
  { x: 500, y: 300, width: 2, startPoint: true, endPoint: true, segments: [[43, -90], [5, -45], [40, 0]] },
  { x: 490, y: 300, width: 2, startPoint: false, endPoint: false, segments: [[55, -90], [5, -45], [60, 0],
    [20, -45], [78, -90]] },
  { x: 480, y: 300, width: 2, startPoint: false, endPoint: false, segments: [[65, -90], [5, -45], [67, 0],
    [10, -45], [75, -90]] },
  { x: 470, y: 300, width: 2, startPoint: false, endPoint: false, segments: [[75, -90], [5, -45], [70, 0],
    [5, -45], [70, -90]] },
  { x: 530, y: 165, width: 2, startPoint: true, endPoint: true, segments: [[30, 90], [20, 135], [40, 180],
    [63, 225]] },
  { x: 450, y: 165, width: 2, startPoint: true, endPoint: true, segments: [[40, 45], [30, 0]] },
  { x: 470, y: 165, width: 2, startPoint: true, endPoint: true, segments: [[20, 45], [25, 0]] },
  { x: 515, y: 265, width: 2, startPoint: true, endPoint: true, segments: [[25, 0], [10, 45], [15, 0]] },
  { x: 515, y: 280, width: 2, startPoint: true, endPoint: true, segments: [[20, 0], [10, 45], [20, 0]] },
  { x: 670, y: 290, width: 2, startPoint: true, endPoint: true, segments: [[15, -225], [20, 90], [10, 45],
    [10, 0], [40, -45]] },
  { x: 670, y: 390, width: 2, startPoint: false, endPoint: true, segments: [[40, -90], [30, -135], [40, -90],
    [50, -45]] },
  { x: 740, y: 340, width: 2, startPoint: true, endPoint: true, segments: [[30, -45], [30, -90], [25, -135],
    [30, -180], [50, -225]] },
  { x: 725, y: 255, width: 2, startPoint: true, endPoint: false, segments: [[23, -90]] },
  // Frame
  { x: 380, y: 150, width: 2, startPoint: false, endPoint: false, segments: [[60, 90], [10, 135], [100, 180]] },
  // Pins, top to bottom
  { x: 380, y: 160, width: 2, startPoint: false, endPoint: false, segments: [[10, 0], [12, -45]] },
  { x: 380, y: 160, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  { x: 380, y: 170, width: 2, startPoint: false, endPoint: false, segments: [[15, 0], [25, -45]] },
  { x: 380, y: 170, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  { x: 380, y: 180, width: 2, startPoint: false, endPoint: false, segments: [[20, 0], [40, -45]] },
  { x: 380, y: 180, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  { x: 380, y: 190, width: 2, startPoint: false, endPoint: true, segments: [[25, 0], [20, -45], [5, 0],
    [50, 45]] },
  { x: 380, y: 190, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  { x: 380, y: 200, width: 2, startPoint: false, endPoint: true, segments: [[30, 0], [10, -45], [5, 0],
    [50, 45]] },
  { x: 380, y: 200, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  // Pins, right to left
  { x: 360, y: 217, width: 2, startPoint: false, endPoint: true, segments: [[30, 90], [10, 45], [15, 0],
    [50, 45], [15, 0], [35, -45]] },
  { x: 360, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 350, y: 217, width: 2, startPoint: false, endPoint: false, segments: [[40, 90], [15, 45], [15, 0],
    [48, 45]] },
  { x: 350, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 340, y: 217, width: 2, startPoint: false, endPoint: false, segments: [[50, 90], [20, 45], [15, 0],
    [43, 45]] },
  { x: 340, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 330, y: 217, width: 2, startPoint: false, endPoint: false, segments: [[120, 90], [15, 45], [23, 90]] },
  { x: 330, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 320, y: 217, width: 2, startPoint: false, endPoint: true, segments: [[100, 90], [25, 135], [55, 90]] },
  { x: 320, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 310, y: 217, width: 2, startPoint: false, endPoint: true, segments: [[90, 90], [35, 135], [75, 90]] },
  { x: 310, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 300, y: 217, width: 2, startPoint: false, endPoint: true, segments: [[70, 90]] },
  { x: 300, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 290, y: 217, width: 2, startPoint: false, endPoint: true, segments: [[80, 90], [10, 45]] },
  { x: 290, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 280, y: 217, width: 2, startPoint: false, endPoint: true, segments: [[60, 90], [70, 135], [130, 90]] },
  { x: 280, y: 217, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 270, y: 222, width: 2, startPoint: false, endPoint: true, segments: [[50, 90], [70, 135], [140, 90],
    [60, 135]] },
  { x: 270, y: 222, width: 4, startPoint: false, endPoint: false, segments: [[5, 90]] },
  { x: 260, y: 250, width: 2, startPoint: true, endPoint: true, segments: [[55, 135]] },
  { x: 260, y: 265, width: 2, startPoint: true, endPoint: true, segments: [[55, 135]] },
  { x: 280, y: 320, width: 2, startPoint: false, endPoint: false, segments: [[15, -90], [5, -135], [5, -180],
    [45, -225], [15, -270], [5, -315], [5, -360], [45, -45]] },
  { x: 290, y: 500, width: 2, startPoint: true, endPoint: true, segments: [[30, -180], [40, -135], [5, -180],
    [139, -225]] },
  // Chip 2
  { x: 620, y: 670, width: 2, startPoint: false, endPoint: false, segments: [[10, 0], [15, 45], [52, 90],
    [18, 135], [156, 180], [18, 225], [20, 270]] },
  // Chip 2 pins, top to bottom and right
  { x: 640, y: 690, width: 2, startPoint: false, endPoint: true, segments: [[30, 0], [95, -45], [60, 0],
    [25, -45]] },
  { x: 640, y: 690, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  { x: 640, y: 700, width: 2, startPoint: false, endPoint: true, segments: [[35, 0], [95, -45], [80, 0],
    [25, 45]] },
  { x: 640, y: 700, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  { x: 640, y: 710, width: 2, startPoint: false, endPoint: false, segments: [[50, 0], [35, 45], [67, 0]] },
  { x: 640, y: 710, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  { x: 640, y: 720, width: 2, startPoint: false, endPoint: true, segments: [[45, 0], [35, 45], [40, 0],
    [14, 45]] },
  { x: 640, y: 720, width: 4, startPoint: false, endPoint: false, segments: [[10, 0]] },
  { x: 620, y: 746, width: 2, startPoint: false, endPoint: true, segments: [[39, 90], [35, 45], [40, 0],
    [35, -45]] },
  { x: 620, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 610, y: 746, width: 2, startPoint: false, endPoint: false, segments: [[44, 90], [45, 45], [40, 0],
    [5, 45]] },
  { x: 610, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 600, y: 746, width: 2, startPoint: false, endPoint: false, segments: [[50, 90], [45, 45], [44, 90]] },
  { x: 600, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 590, y: 746, width: 2, startPoint: false, endPoint: false, segments: [[55, 90], [45, 45], [39, 90]] },
  { x: 590, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 580, y: 746, width: 2, startPoint: false, endPoint: true, segments: [[60, 90], [45, 45]] },
  { x: 580, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 570, y: 746, width: 2, startPoint: false, endPoint: true, segments: [[65, 90], [38, 45]] },
  { x: 570, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 560, y: 746, width: 2, startPoint: false, endPoint: true, segments: [[70, 90], [52, 45]] },
  { x: 560, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 550, y: 746, width: 2, startPoint: false, endPoint: true, segments: [[75, 90], [65, 45], [12.5, 0],
    [5, -45], [10, -90]] },
  { x: 550, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 540, y: 746, width: 2, startPoint: false, endPoint: false, segments: [[45, 90], [25, 135], [30, 90],
    [10, 45], [30, 90]] },
  { x: 540, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 530, y: 746, width: 2, startPoint: false, endPoint: false, segments: [[40, 90], [25, 135], [35, 90],
    [10, 135], [30, 90]] },
  { x: 530, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 520, y: 746, width: 2, startPoint: false, endPoint: true, segments: [[35, 90], [25, 135], [30, 90]] },
  { x: 520, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 510, y: 746, width: 2, startPoint: false, endPoint: false, segments: [[30, 90], [25, 135], [78, 90]] },
  { x: 510, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 500, y: 746, width: 2, startPoint: false, endPoint: false, segments: [[25, 90], [25, 135], [83, 90]] },
  { x: 500, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 490, y: 746, width: 2, startPoint: false, endPoint: true, segments: [[20, 90], [85, 135], [20, 90],
    [25, 45]] },
  { x: 490, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 480, y: 746, width: 2, startPoint: false, endPoint: true, segments: [[15, 90], [85, 135], [45, 180],
    [35, 135]] },
  { x: 480, y: 746, width: 4, startPoint: false, endPoint: false, segments: [[10, 90]] },
  { x: 460, y: 720, width: 2, startPoint: false, endPoint: true, segments: [[35, 180], [65, 135], [25, 180],
    [35, 135]] },
  { x: 460, y: 720, width: 4, startPoint: false, endPoint: false, segments: [[10, 180]] },
  { x: 415, y: 710, width: 2, startPoint: false, endPoint: true, segments: [[35, 135], [15, 180], [15, 135]] },
  { x: 400, y: 700, width: 2, startPoint: false, endPoint: true, segments: [[65, 180], [45, 225], [25, 180],
    [15, 135], [25, 180]] },
  { x: 390, y: 690, width: 2, startPoint: false, endPoint: true, segments: [[50, 180], [45, 225], [40, 180],
    [15, 135], [25, 180]] },
]
